import React from 'react'
import {Link} from 'react-router-dom'
import {useBrandingContext} from '../BrandingProvider'
import {useSiteContent} from '../../hooks/useSiteContent'
import {useAnalytics} from '../../hooks/useAnalytics'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {FiBookOpen, FiMail, FiPhone, FiMapPin, FiInstagram, FiFacebook, FiLinkedin} = FiIcons

const Footer = () => {
  const {branding} = useBrandingContext()
  const {content: siteContent} = useSiteContent()
  const {trackCTAClick} = useAnalytics()

  const defaultLinks = [
    {label: 'Privacy Policy', url: '/privacy'},
    {label: 'Terms of Service', url: '/terms'},
    {label: 'Contact', url: 'mailto:laura@empowernutrition.info'}
  ]

  const footerLinks = siteContent?.footer_links || branding?.footer_links || defaultLinks

  const handleLinkClick = (label, url) => {
    trackCTAClick(`footer_link_${label.toLowerCase().replace(/\s+/g, '_')}`, 'footer', url)
  }

  const handleSocialClick = (platform) => {
    trackCTAClick(`social_${platform}`, 'footer')
  }

  return (
    <footer 
      className="text-white"
      style={{backgroundColor: branding?.primary_color || '#272727'}}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              {branding?.logo_url ? (
                <img
                  src={branding.logo_url}
                  alt="Empower Nutrition Logo"
                  className="h-10 w-auto filter brightness-0 invert"
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{backgroundColor: branding?.secondary_color || '#a0cfbc'}}
                >
                  <SafeIcon 
                    icon={FiBookOpen} 
                    className="w-6 h-6"
                    style={{color: branding?.primary_color || '#272727'}}
                  />
                </div>
              )}
              <span 
                className="text-xl font-semibold"
                style={{fontFamily: `var(--font-heading, ${branding?.heading_font || 'Inter'})`}}
              >
                Empower Nutrition
              </span>
            </div>
            <p 
              className="text-gray-300 mb-6 max-w-md leading-relaxed"
              style={{fontFamily: `var(--font-body, ${branding?.body_font || 'Inter'})`}}
            >
              Transform your understanding of nutrition with our comprehensive courses designed by experts. 
              Learn sustainable habits that work with your real life.
            </p>

            {/* Contact Info */}
            <div className="space-y-2 mb-6">
              <div className="flex items-center space-x-3 text-gray-300">
                <SafeIcon icon={FiMail} className="w-4 h-4" />
                <a
                  href="mailto:laura@empowernutrition.info"
                  onClick={() => handleLinkClick('email', 'mailto:laura@empowernutrition.info')}
                  className="hover:text-white transition-colors"
                >
                  laura@empowernutrition.info
                </a>
              </div>
            </div>

            {/* Social Media */}
            <div className="flex items-center space-x-4">
              <a
                href="https://instagram.com/empowernutrition"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleSocialClick('instagram')}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Follow us on Instagram"
              >
                <SafeIcon icon={FiInstagram} className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com/empowernutrition"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleSocialClick('facebook')}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Follow us on Facebook"
              >
                <SafeIcon icon={FiFacebook} className="w-5 h-5" />
              </a>
              <a
                href="https://linkedin.com/company/empowernutrition"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleSocialClick('linkedin')}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Follow us on LinkedIn"
              >
                <SafeIcon icon={FiLinkedin} className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 
              className="text-lg font-semibold mb-4"
              style={{fontFamily: `var(--font-heading, ${branding?.heading_font || 'Inter'})`}}
            >
              Quick Links
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  onClick={() => handleLinkClick('Home', '/')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/courses"
                  onClick={() => handleLinkClick('Courses', '/courses')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Courses
                </Link>
              </li>
              <li>
                <a
                  href={siteContent?.cta_secondary_url || 'https://tidycal.com/empowernutrition/30-minute-consultation'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleLinkClick('Consultation', siteContent?.cta_secondary_url)}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {siteContent?.transformation_cta_label || 'Consultation'}
                </a>
              </li>
            </ul>
          </div>

          {/* Dynamic Footer Links */}
          <div>
            <h3 
              className="text-lg font-semibold mb-4"
              style={{fontFamily: `var(--font-heading, ${branding?.heading_font || 'Inter'})`}}
            >
              Support
            </h3>
            <ul className="space-y-2">
              {footerLinks.map((link, index) => (
                <li key={index}>
                  {link.url.startsWith('http') || link.url.startsWith('mailto:') ? (
                    <a
                      href={link.url}
                      target={link.url.startsWith('mailto:') ? '_self' : '_blank'}
                      rel={link.url.startsWith('mailto:') ? '' : 'noopener noreferrer'}
                      onClick={() => handleLinkClick(link.label, link.url)}
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.url}
                      onClick={() => handleLinkClick(link.label, link.url)}
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
          <p 
            style={{fontFamily: `var(--font-body, ${branding?.body_font || 'Inter'})`}}
          >
            &copy; 2024 Empower Nutrition. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer